import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import CourseDetail from "./pages/CourseDetail";
import Calendar from "./pages/Calendar";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminCourses from "./pages/admin/Courses";
import AdminInstructors from "./pages/admin/Instructors";
import AdminBookings from "./pages/admin/Bookings";
import AdminUsers from "./pages/admin/Users";
import AdminBookingHistory from "./pages/admin/BookingHistory";
import InstructorDashboard from "./pages/admin/InstructorDashboard";
import AdminReports from "./pages/admin/Reports";
import AdminSessionManagement from "./pages/admin/SessionManagement";
import AdminNotificationLogs from "./pages/admin/NotificationLogs";
import AdminTemplates from "./pages/admin/Templates";
import Settings from "./pages/Settings";
import SelfServiceLogin from "./pages/SelfServiceLogin";
import SelfServicePortal from "./pages/SelfServicePortal";
import GymDashboard from "./pages/admin/gym/GymDashboard";
import GymServices from "./pages/admin/gym/GymServices";
import GymSlotsCalendar from "./pages/admin/gym/GymSlotsCalendar";
import GymBookings from "./pages/admin/gym/GymBookings";
import GymMembers from "./pages/admin/gym/GymMembers";
import GymReports from "./pages/admin/gym/GymReports";
import GymTemplates from "./pages/admin/gym/GymTemplates";
import GymStatistics from "./pages/admin/GymStatistics";
import GymLanding from "./pages/gym/GymLanding";
import GymCalendar from "./pages/gym/GymCalendar";
import GymRegister from "./pages/gym/GymRegister";
import GymRegisterComplete from "./pages/gym/GymRegisterComplete";
import MyBookings from "./pages/gym/MyBookings";
import MyStats from "./pages/gym/MyStats";
import MyCourses from "./pages/MyCourses";
import { useTenantTheme } from "./hooks/useTenantTheme";
import { TenantSwitcher } from "./components/TenantSwitcher";
import { FeatureGuard } from "./components/FeatureGuard";
import { useTrackingInit } from "./hooks/useTracking";
import { SeedDataBanner } from "./components/SeedDataBanner";

// Wrapper components for feature-guarded routes
function GymRoute({ component: Component }: { component: React.ComponentType }) {
  return (
    <FeatureGuard feature="gymEnabled" redirectTo="/">
      <Component />
    </FeatureGuard>
  );
}

function SelfServiceRoute({ component: Component }: { component: React.ComponentType }) {
  return (
    <FeatureGuard feature="selfServiceEnabled" redirectTo="/">
      <Component />
    </FeatureGuard>
  );
}

function Router() {
  return (
    <Switch>
      {/* Public Routes */}
      <Route path="/" component={Home} />
      <Route path="/course/:id" component={CourseDetail} />
      <Route path="/course/:id/session/:sessionId" component={CourseDetail} />
      <Route path="/calendar" component={Calendar} />
      <Route path="/my-courses" component={MyCourses} />

      {/* Self-Service Portal - login page stays, portal redirects to /my-courses */}
      <Route path="/self-service/login">
        <SelfServiceRoute component={SelfServiceLogin} />
      </Route>
      <Route path="/self-service">
        <SelfServiceRoute component={SelfServicePortal} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" component={AdminDashboard} />
      <Route path="/admin/courses" component={AdminCourses} />
      <Route path="/admin/instructors" component={AdminInstructors} />
      <Route path="/admin/bookings" component={AdminBookings} />
      <Route path="/admin/users" component={AdminUsers} />
      <Route path="/admin/settings" component={Settings} />
      <Route path="/admin/booking-history" component={AdminBookingHistory} />
      <Route path="/admin/my-courses" component={InstructorDashboard} />
      <Route path="/admin/reports" component={AdminReports} />
      <Route path="/admin/sessions" component={AdminSessionManagement} />
      <Route path="/admin/notifications" component={AdminNotificationLogs} />
      <Route path="/admin/templates" component={AdminTemplates} />

      {/* Gym Public Routes - guarded by gymEnabled */}
      <Route path="/gym">
        <GymRoute component={GymLanding} />
      </Route>
      <Route path="/gym/calendar">
        <GymRoute component={GymCalendar} />
      </Route>
      <Route path="/gym/register">
        <GymRoute component={GymRegister} />
      </Route>
      <Route path="/gym/register/complete">
        <GymRoute component={GymRegisterComplete} />
      </Route>
      <Route path="/gym/bookings">
        <GymRoute component={MyBookings} />
      </Route>
      <Route path="/gym/stats">
        <GymRoute component={MyStats} />
      </Route>

      {/* Gym Admin Routes - guarded by gymEnabled */}
      <Route path="/admin/gym">
        <GymRoute component={GymDashboard} />
      </Route>
      <Route path="/admin/gym/services">
        <GymRoute component={GymServices} />
      </Route>
      <Route path="/admin/gym/slots">
        <GymRoute component={GymSlotsCalendar} />
      </Route>
      <Route path="/admin/gym/bookings">
        <GymRoute component={GymBookings} />
      </Route>
      <Route path="/admin/gym/members">
        <GymRoute component={GymMembers} />
      </Route>
      <Route path="/admin/gym/reports">
        <GymRoute component={GymReports} />
      </Route>
      <Route path="/admin/gym/templates">
        <GymRoute component={GymTemplates} />
      </Route>
      <Route path="/admin/gym/statistics">
        <GymRoute component={GymStatistics} />
      </Route>

      {/* Fallback */}
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  // Load tenant configuration and apply dynamic theming
  useTenantTheme();

  // Initialize PostHog tracking with tenant context and user identity
  useTrackingInit();

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <SeedDataBanner />
          <Router />
          <TenantSwitcher />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
